import { flowField, enemyEnterCost, enemyGoals, tracePaths } from './path';
import type { Profile } from './profile';
import { createRng, nextInt, seedFromString, shuffle, type RngState } from './rng';
import type { Balance, CellState, ChapterDef, EnemyArchId, MapDef, SpawnGroup, WaveDef } from './types';

/**
 * The level ladder: chapters of ten short levels each, generated from the level number alone.
 * Everything here is integer math on the sim PRNG, so the server rebuilds the exact same board
 * and waves the client played. Authored maps sit at the end of chapters as finales.
 */

export const LEVEL_ID_RE = /^l(\d{1,3})$/;

export interface LevelRef {
  /** 1-based number across the whole ladder. */
  n: number;
  /** 1-based chapter. */
  chapter: number;
  /** 1-based position inside the chapter. */
  index: number;
  /** Map id to play: `l<n>` or the chapter's authored finale. */
  mapId: string;
  finale: boolean;
}

export function ladderLength(balance: Balance): number {
  return balance.ladder.chapters.reduce((a, c) => a + c.levels, 0);
}

export function chapterOf(n: number, balance: Balance): { def: ChapterDef; chapter: number; index: number } | null {
  let start = 0;
  for (let c = 0; c < balance.ladder.chapters.length; c++) {
    const def = balance.ladder.chapters[c]!;
    if (n > start && n <= start + def.levels) return { def, chapter: c + 1, index: n - start };
    start += def.levels;
  }
  return null;
}

export function levelRef(n: number, balance: Balance): LevelRef | null {
  const c = chapterOf(n, balance);
  if (!c) return null;
  const finale = c.index === c.def.levels && c.def.finale !== undefined;
  return { n, chapter: c.chapter, index: c.index, mapId: finale ? c.def.finale! : `l${n}`, finale };
}

/** The ladder position of a map id (generated `l<n>` or an authored finale), or null for maps outside the ladder. */
export function levelOfMap(mapId: string, balance: Balance): LevelRef | null {
  const m = LEVEL_ID_RE.exec(mapId);
  if (m) {
    const n = Number(m[1]);
    const ref = levelRef(n, balance);
    return ref && !ref.finale ? ref : null;
  }
  let start = 0;
  for (const def of balance.ladder.chapters) {
    if (def.finale === mapId) return levelRef(start + def.levels, balance);
    start += def.levels;
  }
  return null;
}

/** Level n is playable when it is the first or the previous level has been won. */
export function ladderUnlocked(p: Profile, n: number, balance: Balance): boolean {
  if (n <= 1) return true;
  const prev = levelRef(n - 1, balance);
  return prev !== null && p.maps[prev.mapId]?.won === true;
}

/** Highest unlocked level: where the player is on the ladder. */
export function ladderPosition(p: Profile, balance: Balance): number {
  const total = ladderLength(balance);
  let n = 1;
  while (n < total && ladderUnlocked(p, n + 1, balance)) n++;
  return n;
}

/** Stars for a win by how much of the bunker survived: 3 at 70 %+, 2 at 35 %+, else 1. */
export function starsFor(wallHp: number, wallMax: number): number {
  if (wallMax <= 0) return 1;
  if (wallHp * 10 >= wallMax * 7) return 3;
  if (wallHp * 20 >= wallMax * 7) return 2;
  return 1;
}

function pick<T>(rng: RngState, arr: readonly T[]): T {
  return arr[nextInt(rng, arr.length)]!;
}

/** Percent HP for wave w (1-based) of a level: chapter start, per-level growth, per-wave compounding. */
function waveHp(def: ChapterDef, index: number, w: number): number {
  let hp = Math.floor((def.hpStart * (100 + def.hpPerLevelPct * (index - 1))) / 100);
  for (let i = 1; i < w; i++) hp = Math.floor((hp * def.hpGrowthPct) / 100);
  return hp;
}

function makeWaves(def: ChapterDef, index: number, rng: RngState): WaveDef[] {
  const count = def.wavesMin + Math.floor(((index - 1) * (def.wavesMax - def.wavesMin)) / Math.max(1, def.levels - 1));
  const has = (a: EnemyArchId): boolean => def.roster.includes(a);
  const waves: WaveDef[] = [];
  const bossLevel = index % 5 === 0;
  for (let w = 1; w <= count; w++) {
    const hp = waveHp(def, index, w);
    const grow = Math.floor(index / 2) + Math.floor(w / 2);
    const spacing = Math.max(6, 14 - Math.floor(w / 2));
    const g = (arch: EnemyArchId, n: number, startTick: number, sp: number): SpawnGroup => ({ arch, count: Math.max(1, Math.min(40, n)), startTick, spacing: sp, hpPct: hp });
    const spawns: SpawnGroup[] = [];
    const pattern = w % 4;
    if (w === 1 || pattern === 0) spawns.push(g('walker', 5 + grow, 0, spacing));
    else if (pattern === 1) {
      spawns.push(g('walker', 5 + grow, 0, spacing));
      if (has('runner')) spawns.push(g('runner', 3 + grow, 30, 8));
    } else if (pattern === 2) {
      if (has('brute')) spawns.push(g('brute', 1 + Math.floor(grow / 2), 0, 50));
      spawns.push(g('walker', 6 + grow, 15, spacing));
      if (has('flyer') && w >= 3) spawns.push(g('flyer', 2 + Math.floor(grow / 2), 60, 10));
    } else {
      spawns.push(g('walker', 6 + grow, 0, spacing));
      if (has('runner')) spawns.push(g('runner', 4 + grow, 40, 7));
      if (has('armored') && w >= 4) spawns.push(g('armored', 1 + Math.floor(grow / 3), 70, 40));
      else if (has('brute')) spawns.push(g('brute', 1 + Math.floor(grow / 3), 70, 45));
      if (has('flyer') && w >= 3) spawns.push(g('flyer', 2 + Math.floor(grow / 3), 90, 10));
    }
    if (bossLevel && w === count) {
      // A ladder boss is a wave-end punctuation, not a wall: 45 % of the wave's HP curve, 40 % each when there are two.
      const bosses = def.doubleBoss ? 2 : 1;
      const boss: SpawnGroup = { ...g('boss', bosses, 50, 100), hpPct: Math.floor((hp * (bosses === 2 ? 40 : 45)) / 100) };
      if (def.bossAbilities && def.bossAbilities.length > 0) boss.ability = pick(rng, def.bossAbilities);
      spawns.push(boss);
    }
    waves.push({ spawns });
  }
  return waves;
}

type Base = NonNullable<MapDef['base']>;

function gridOf(width: number, height: number, closed: MapDef['closed'], base: Base): CellState[] {
  const grid: CellState[] = [];
  for (let i = 0; i < width * height; i++) grid.push({ kind: 'empty' });
  for (const c of closed) grid[c.y * width + c.x] = { kind: 'closed', cost: c.cost };
  for (let y = base.y; y < base.y + base.h; y++) for (let x = base.x; x < base.x + base.w; x++) grid[y * width + x] = { kind: 'base' };
  return grid;
}

function roadOf(width: number, height: number, grid: CellState[]): { open: boolean; onRoute: Set<number>; length: number } {
  const alive = (): boolean => false;
  const cost = enemyEnterCost(grid, alive);
  const field = flowField(width, height, enemyGoals(grid, width, height), cost);
  const paths = tracePaths(width, height, field, cost, alive);
  const onRoute = new Set<number>();
  for (const r of paths.routes) for (const i of r) onRoute.add(i);
  const length = paths.routes[0]?.length ?? 0;
  return { open: paths.spawnColumns.length > 0 && paths.routes.length > 0 && length > 0, onRoute, length };
}

/** Cells enemies can reach the base from: the board must keep a road from some top cell to the bunker. */
export function boardOpen(width: number, height: number, closed: MapDef['closed'], base: Base): boolean {
  return roadOf(width, height, gridOf(width, height, closed, base)).open;
}

/** Whether a cell touches the road, diagonals included: a gun on a corner cell covers the bend as well. */
function touchesRoad(width: number, height: number, x: number, y: number, onRoute: Set<number>): boolean {
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = x + dx;
      const ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
      if (onRoute.has(ny * width + nx)) return true;
    }
  }
  return false;
}

/**
 * Keep at least `minSide` free zone cells right next to the road (not on it): a gun or a camp works
 * from there, while a free corner two cells away does nothing and a board whose only free cells are
 * the corridor offers no placement that does not block. Levels generated without this rule were walls
 * (zero cells by the road on l8, l32, l47) regardless of their wave numbers.
 * Removes one rock at a time, the one whose removal gives the most cells by the road while keeping the
 * road as long as possible (the maze is the point), nearest the base and cheapest on ties, so a board
 * loses as few rocks as it can. Shared by the level generator and the authored-map tool.
 */
export function thinRocksBesideRoad(width: number, height: number, closed: MapDef['closed'], base: Base, firstBuildRow: number, minSide: number): void {
  const measure = (rocks: MapDef['closed']): { side: number; length: number; onRoute: Set<number> } => {
    const grid = gridOf(width, height, rocks, base);
    const road = roadOf(width, height, grid);
    let side = 0;
    for (let y = firstBuildRow; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = y * width + x;
        if (grid[i]!.kind !== 'empty' || road.onRoute.has(i)) continue;
        if (touchesRoad(width, height, x, y, road.onRoute)) side++;
      }
    }
    return { side, length: road.length, onRoute: road.onRoute };
  };
  for (let guard = 0; guard < 32; guard++) {
    const cur = measure(closed);
    if (cur.side >= minSide) return;
    let best = -1;
    let bestKey = -Infinity;
    for (let k = 0; k < closed.length; k++) {
      const r = closed[k]!;
      if (r.y < firstBuildRow) continue;
      if (!touchesRoad(width, height, r.x, r.y, cur.onRoute)) continue;
      const trial = closed.filter((_, j) => j !== k);
      const m = measure(trial);
      const key = m.side * 10000 + m.length * 100 + r.y * 10 - Math.min(9, Math.floor(r.cost / 5));
      if (key > bestKey) {
        bestKey = key;
        best = k;
      }
    }
    if (best < 0) return;
    closed.splice(best, 1);
  }
}

/**
 * Build level n (never a finale: those are authored maps resolved by the content package).
 * Rocks are placed by the level's own seed, then thinned from the last placed until a road exists.
 */
export function levelMap(n: number, balance: Balance): MapDef | null {
  const ref = levelRef(n, balance);
  if (!ref || ref.finale) return null;
  const { def, index } = chapterOf(n, balance)!;
  const rng = createRng(seedFromString(`level:${n}`));
  const width = def.width;
  const height = def.height;
  const base = { x: (width - 2) >> 1, y: height - 1, w: 2, h: 1 };
  // Tight board (5 October): everything below the two enemy rows is buildable from wave 1 and stays so;
  // the zone does not grow. Most of it starts under rocks, and clearing them is where the gold goes.
  const buildRows = height - 2;
  const firstBuildRow = height - buildRows;
  const closed: MapDef['closed'] = [];

  // Permanent rocks in the enemy rows, never on the spawn row itself so the horde keeps its columns.
  const top: number[] = [];
  for (let y = 1; y < firstBuildRow; y++) for (let x = 0; x < width; x++) top.push(y * width + x);
  shuffle(rng, top);
  const topWant = def.topRocks[0] + nextInt(rng, def.topRocks[1] - def.topRocks[0] + 1);
  for (const i of top.slice(0, Math.min(topWant, top.length))) closed.push({ x: i % width, y: Math.floor(i / width), cost: 0 });

  // Zone rocks: never the base's neighbours on the bottom row, so a road to the bunker always exists.
  const zone: number[] = [];
  for (let y = firstBuildRow; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (y === base.y && x >= base.x - 1 && x <= base.x + base.w) continue;
      zone.push(y * width + x);
    }
  }
  // Rocks thin out near the bunker and thicken toward the enemy: the first buildings have room by the
  // base, expansion toward the front is bought with gold. Integer keys, stable order, same on the server.
  const keyed = zone.map((i) => ({ i, key: nextInt(rng, 100) + (height - 1 - Math.floor(i / width)) * 12 }));
  keyed.sort((a, b) => b.key - a.key || a.i - b.i);
  const want = Math.floor((zone.length * def.zoneRocksPct + 50) / 100);
  const steps = Math.floor((def.rockCost[1] - def.rockCost[0]) / 5) + 1;
  let placed = 0;
  for (const { i } of keyed) {
    if (placed >= want) break;
    const permanent = nextInt(rng, 100) < def.zonePermanentPct;
    const cost = permanent ? 0 : def.rockCost[0] + 5 * nextInt(rng, steps);
    const rock = { x: i % width, y: Math.floor(i / width), cost };
    // A rock that would wall the bunker off is simply not placed; the horde always has a road.
    if (!boardOpen(width, height, [...closed, rock], base)) continue;
    closed.push(rock);
    placed++;
  }
  // Space to build beside the road, not only on it.
  thinRocksBesideRoad(width, height, closed, base, firstBuildRow, width <= 5 ? 3 : 4);

  return {
    id: `l${n}`,
    width,
    height,
    closed,
    base,
    buildRows,
    buildRowsPerWave: 0,
    waves: makeWaves(def, index, rng),
    startGold: def.startGold,
    wallHp: def.wallHp,
  };
}
